import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { NavigationContainerRef } from '@react-navigation/native';

interface WebBackButtonHandlerProps {
  navigationRef: React.RefObject<NavigationContainerRef<any>>;
  onStateChange?: (state: any) => void;
}

/**
 * Handles browser back button on web/mobile web
 * Intercepts popstate events and navigates through React Navigation stack
 * instead of immediately exiting the website
 */
const WebBackButtonHandler: React.FC<WebBackButtonHandlerProps> = ({ 
  navigationRef,
  onStateChange 
}) => {
  const isHandlingBackRef = useRef(false);
  const lastDepthRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  // Calculate a simple \"depth\" value from the navigation state
  const getNavigationDepth = (state: any): number => {
    if (!state) {
      return 0;
    }

    let depth = 0;
    let current: any = state;

    // Walk down nested navigators and sum their indices
    while (current && typeof current.index === 'number' && Array.isArray(current.routes)) {
      depth += current.index;
      const activeRoute = current.routes[current.index];
      if (activeRoute && activeRoute.state) {
        current = activeRoute.state;
      } else {
        break;
      }
    }

    return depth;
  };

  useEffect(() => {
    // Only run on web
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    // Initialize browser history
    const initializeHistory = () => {
      try {
        const nav = navigationRef.current;
        if (nav) {
          const state = nav.getState();
          const depth = getNavigationDepth(state);
          lastDepthRef.current = depth;
          // IMPORTANT:
          // Use pushState instead of replaceState so that there are
          // at least TWO history entries for this URL.
          // On mobile, if there is only one entry, the first back press
          // closes the tab without firing popstate. With two entries,
          // back fires popstate and we can intercept it.
          window.history.pushState({ 
            navigation: true, 
            depth 
          }, '', window.location.href);
          isInitializedRef.current = true;
        }
      } catch (err) {
        // Ignore errors
      }
    };

    // Initialize after navigation is ready
    let initAttempts = 0;
    const tryInit = () => {
      if (navigationRef.current?.getState() && !isInitializedRef.current) {
        initializeHistory();
      } else if (initAttempts < 10) {
        initAttempts++;
        setTimeout(tryInit, 100);
      }
    };
    tryInit();

    const handlePopState = (event: PopStateEvent) => {
      // If we're already handling a back navigation, ignore
      if (isHandlingBackRef.current) {
        return;
      }

      const nav = navigationRef.current;
      if (!nav || !isInitializedRef.current) {
        return;
      }

      // Check if we can go back in the navigation stack
      const canGoBack = nav.canGoBack();

      if (canGoBack) {
        // Navigate back in the app
        isHandlingBackRef.current = true;
        nav.goBack();

        // After navigation completes, update history
        setTimeout(() => {
          try {
            const state = nav.getState();
            const depth = getNavigationDepth(state);
            lastDepthRef.current = depth;
            // Push new state to maintain history stack
            window.history.pushState({ 
              navigation: true, 
              depth 
            }, '', window.location.href);
          } catch (err) {
            // Ignore errors
          }
          isHandlingBackRef.current = false;
        }, 150);
      } else {
        // We're at the root - prevent exit by pushing a new state
        try {
          window.history.pushState({ 
            navigation: true, 
            depth: 0 
          }, '', window.location.href);
          lastDepthRef.current = 0;
        } catch (err) {
          // Ignore errors
        }
      }
    };

    // Listen to popstate events (browser back/forward buttons)
    window.addEventListener('popstate', handlePopState);

    // Track navigation state changes
    const handleNavigationStateChange = (state: any) => {
      // Call the provided callback first
      if (onStateChange) {
        onStateChange(state);
      }

      // Skip if we're handling a back navigation or not initialized
      if (isHandlingBackRef.current || !isInitializedRef.current) {
        return;
      }

      try {
        const nav = navigationRef.current;
        if (!nav) return;

        const stateFromNav = nav.getState();
        const depth = getNavigationDepth(stateFromNav);

        // If depth increased, user navigated forward -> push a new history entry
        if (depth > lastDepthRef.current) {
          lastDepthRef.current = depth;
          window.history.pushState({ 
            navigation: true, 
            depth 
          }, '', window.location.href);
        } else if (depth !== lastDepthRef.current) {
          // Depth decreased (went back) or changed some other way -> just track it
          lastDepthRef.current = depth;
        }
      } catch (err) {
        // Ignore errors
      }
    };

    // Check periodically as fallback to catch navigation changes
    const intervalId = setInterval(() => {
      const nav = navigationRef.current;
      if (nav && !isHandlingBackRef.current && isInitializedRef.current) {
        const state = nav.getState();
        if (state) {
          handleNavigationStateChange(state);
        }
      }
    }, 250);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(intervalId);
    };
  }, [navigationRef, onStateChange]);

  return null;
};

export default WebBackButtonHandler;
